import React, { useCallback } from 'react';
import { conformToMask } from 'react-text-mask';
import { MASKS } from '../../../constants/constants';

const isPhoneDigitChar = (char) => /[A-Z0-9]/i.test(char || '');

/**
 *  Mask one of  ['phone', 'ext']
 * @default phone
 * */
export default function MaskedInput({
  mask = 'phone',
  value,
  onChange,
  onKeyDown,
  ...props
}) {
  const handleChange = useCallback(
    (e) => {
      if (mask === 'phone') {
        e.target.value = e.target.value.replace(/[^A-Z0-9]+/gi, '');
      }
      onChange(e);
    },
    [mask, onChange],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
      if (e.defaultPrevented || mask !== 'phone') {
        return;
      }

      const isBackspace = e.key === 'Backspace' || e.keyCode === 8;
      const isDelete = e.key === 'Delete' || e.keyCode === 46;
      if (!isBackspace && !isDelete) {
        return;
      }

      const input = e.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      if (start == null || end == null) {
        return;
      }

      // Select-all / partial selection: keep default + handleChange strip.
      if (start !== end) {
        return;
      }

      const display = String(input.value || '');
      const digits = String(value ?? '').replace(/[^A-Z0-9]+/gi, '');
      if (!digits) {
        return;
      }

      let removeDigitIndex = -1;

      if (isBackspace) {
        if (start === 0) {
          return;
        }
        const charBefore = display[start - 1];
        if (isPhoneDigitChar(charBefore)) {
          return;
        }
        // Mask literal (e.g. trailing '-') — remove preceding digit.
        const digitsBefore = display
          .slice(0, start)
          .replace(/[^A-Z0-9]+/gi, '').length;
        removeDigitIndex = digitsBefore - 1;
      } else {
        if (start >= display.length) {
          return;
        }
        const charAfter = display[start];
        if (isPhoneDigitChar(charAfter)) {
          return;
        }
        // Forward-delete mask literal — remove following digit.
        removeDigitIndex = display
          .slice(0, start)
          .replace(/[^A-Z0-9]+/gi, '').length;
        if (removeDigitIndex >= digits.length) {
          return;
        }
      }

      if (removeDigitIndex < 0 || removeDigitIndex >= digits.length) {
        return;
      }

      e.preventDefault();
      const next =
        digits.slice(0, removeDigitIndex) + digits.slice(removeDigitIndex + 1);
      onChange({
        target: {
          name: input.name,
          value: next,
        },
      });
    },
    [mask, onChange, onKeyDown, value],
  );

  return (
    <input
      className="form-control"
      name="phone"
      value={
        conformToMask(value, MASKS[mask], {
          guide: false,
        }).conformedValue
      }
      maxLength={14}
      {...props}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      type="text"
    />
  );
}
